/**
 * Choosing which of a company's projects of interest become line items on the
 * document about to be raised.
 *
 * 🔴 **THIS DIALOG WRITES NOTHING TO THE COMPANY, AND UNTICKING A ROW DECIDES
 * THIS DOCUMENT ONLY.** There is no `PATCH /companies/{id}` here, no
 * `projectIds` key, and no copy that could be read as taking a project off the
 * company. That write is a **whole-set REPLACE**, and since 2026-09-13 removing
 * a project through it also closes that project's sales pursuit as **Lost** —
 * with `200` and nothing on screen. So the failure would be silent, permanent
 * (nothing records WHY a pursuit is Lost, so a re-tick cannot reopen it) and
 * reachable from a tickbox. **The wording carries the scope**: the title says
 * *this quote / invoice / deal*, and the confirm button counts LINES.
 *
 * ⚠️ **EVERYTHING ARRIVES PRE-TICKED** — owner's decision. The reader is
 * removing rows they do not want, not assembling a document from nothing, so
 * the common case is one press of Continue.
 *
 * ⚠️ **NO HELPER TEXT, and no `Modal` `description`.** Four sentences survive
 * that rule here and every one names a state the reader cannot otherwise see,
 * which is the documented exception: the already-sold badge, the reason a row
 * cannot be chosen, the note that the sold check was skipped, and the note that
 * it partly failed. ⛔ **There is deliberately no sentence explaining that
 * unticking leaves the company alone** — that would be an assertion about the
 * ABSENCE of a write, which is the claim shape this codebase records as rotting
 * fastest, and it would be the only thing on screen suggesting such a write was
 * ever on the table. Fix the wording, never annotate the control.
 *
 * ⚠️ **THERE IS NO PRODUCT PICKER, unlike `CombinePursuitsDialog`.** That dialog
 * seeds ONE line per pursuit, so a project selling several products poses a
 * question. Here the owner's decision is **one line per product per project**,
 * so there is nothing to choose and a `Select` would contradict the rule. The
 * products a row will seed are LISTED instead, in the order they will appear, so
 * the reader can see what a tick costs before pressing Continue.
 *
 * ⚠️ **THIS DIALOG AND THE DOCUMENT FORM ARE NEVER OPEN AT THE SAME TIME.**
 * `CompanyProjectLinesFlow` moves between them, so the nested-modal hazard this
 * codebase has already paid for — a portalled inner form whose submit propagates
 * up the REACT tree to the outer form and creates a half-filled record, with
 * `tsc` and oxlint both green — cannot arise. Do not "improve" this by rendering
 * the form modal underneath.
 */

import { useState } from 'react';

import { Badge, Button, Checkbox, Modal, Skeleton } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import type { SeedableProject } from '@/lib/company-project-lines';
import { cx } from '@/lib/cx';

/** Which create form is being opened. Decides the wording and nothing else. */
export type CompanyProjectLinesKind = 'quote' | 'invoice' | 'deal';

/**
 * Whether this company already has a document naming this project.
 *
 * ⚠️ **`null` IS "NOT KNOWN", AND IT IS NOT `'none'`.** The check was skipped
 * (too many projects to spend the shared rate budget on) or it failed, and those
 * are not evidence that nothing was sold. Rendering `null` as an absence of a
 * badge is only honest because the footnotes below say the check did not run —
 * **an unmarked row must never silently mean "not sold"**.
 *
 * ⚠️ **ANY DOCUMENT COUNTS, WHATEVER ITS STATE** — owner's decision. A draft
 * quote and a paid invoice both mean somebody has already put this project in
 * front of this company, which is the fact the reader is weighing.
 */
export type CompanyProjectSoldMark = 'none' | 'quote' | 'invoice' | 'both';

/**
 * One project of interest, as a row.
 *
 * It extends {@link SeedableProject} rather than restating it so the chosen rows
 * can be handed straight to `seededLinesForProjects` — **there is one line
 * builder and this is not a second one.**
 */
export interface CompanyProjectLinesRow extends SeedableProject {
  /** `null` when the check was skipped or failed. See {@link CompanyProjectSoldMark}. */
  sold: CompanyProjectSoldMark | null;
}

export interface CompanyProjectLinesDialogProps {
  open: boolean;
  onClose: () => void;
  kind: CompanyProjectLinesKind;
  companyName: string;
  /**
   * The company's projects of interest, **in the company's own order**, each
   * with its products already ordered participation-first and travel last.
   *
   * ⚠️ Nothing here re-sorts them: the order a reader sees on the company row is
   * the order the lines arrive in, and a second ordering rule would make the two
   * disagree.
   */
  rows: readonly CompanyProjectLinesRow[];
  /** The reads that name the projects and their products are still in flight. */
  loading: boolean;
  /** What failed, in the server's words where there are any. `null` when nothing did. */
  error: string | null;
  onRetry: () => void;
  /**
   * Go on to the form seeded from these projects — every ticked, readable row.
   *
   * ⚠️ **IT HANDS OVER PROJECTS, NOT A DOCUMENT.** Nothing is created here and
   * nothing is written: the person still meets the form, still edits the rows,
   * and still presses Save. **Prefilling is not raising.** An empty array is a
   * legitimate answer and means *open the form with no lines* — which is exactly
   * what the "+" did before this feature existed.
   */
  onConfirm: (projects: readonly CompanyProjectLinesRow[]) => void;
  /**
   * How many projects the sold check was skipped for, or `null` when it ran.
   *
   * ⚠️ **A FACT ABOUT THE DATA**, the same class as a `Combobox` footnote
   * reporting truncation, and the documented exception to the no-helper-text
   * rule. The check costs two requests per project against a rate limit shared
   * across the whole office, so above a threshold it is not made at all — and
   * without this line an unmarked row reads as *not sold*.
   */
  soldCheckSkippedFor: number | null;
  /** At least one row's sold check failed. Same reasoning as the field above. */
  soldCheckIncomplete: boolean;
}

/**
 * ⚠️ **BOTH FORMS WRITTEN OUT, NEVER DERIVED.** Nothing in this app pluralises
 * client-side — a rule that lives in `@/lib/inclusions` for text a customer is
 * invoiced against, and is worth keeping everywhere for the same reason: the
 * first thing a derived pluraliser does is print `1 lines`.
 */
const KIND_NOUN: Record<CompanyProjectLinesKind, string> = {
  quote: 'quote',
  invoice: 'invoice',
  deal: 'deal',
};

/**
 * ⚠️ **THREE LITERALS, NOT A COMPOSITION.** Assembling "a quote and an invoice"
 * from parts would need an article rule (`a` / `an`) that nothing else in this
 * app has, and the three sentences are cheaper than the rule.
 */
const SOLD_LABEL: Record<Exclude<CompanyProjectSoldMark, 'none'>, string> = {
  quote: 'Already on a quote',
  invoice: 'Already on an invoice',
  both: 'Already on a quote and an invoice',
};

/**
 * A row can be seeded only when its products are known.
 *
 * ⚠️ **`products: []` IS A FAILED READ, NOT A PROJECT THAT SELLS NOTHING.**
 * `d_projects.product_id` stayed `NOT NULL` and the server re-asserts it into the
 * set on every write, so every project sells at least one product. Treating an
 * empty set as a project with nothing to sell would let somebody tick a row that
 * can only ever contribute zero lines, and they would meet an empty grid with no
 * idea why.
 */
function isSeedable(row: CompanyProjectLinesRow): boolean {
  return row.products.length > 0;
}

export function CompanyProjectLinesDialog({
  open,
  onClose,
  kind,
  companyName,
  rows,
  loading,
  error,
  onRetry,
  onConfirm,
  soldCheckSkippedFor,
  soldCheckIncomplete,
}: CompanyProjectLinesDialogProps) {
  /**
   * Only the rows somebody has CHANGED. Absent means "as it arrived".
   *
   * ⚠️ **PRE-TICKING IS DERIVED AT RENDER, NOT SEEDED INTO STATE BY AN EFFECT** —
   * the shape `CombinePursuitsDialog` uses for its product choice, and for the
   * same reason: an effect that writes the initial ticks leaves a window in which
   * the rows are on screen and nothing is ticked, and it can re-answer a question
   * the person has since answered differently. A typed answer always outranks the
   * derivation, which is what checking this map first buys.
   */
  const [ticked, setTicked] = useState<Record<number, boolean>>({});

  function isTicked(row: CompanyProjectLinesRow): boolean {
    const answered = ticked[row.id];
    if (answered !== undefined) return answered;
    // Everything arrives ticked — except a row nothing can be seeded from, which
    // must never be ticked because it would contribute no line and look as
    // though it had.
    return isSeedable(row);
  }

  /*
   * ⚠️ `isSeedable` is re-tested here as well as at the checkbox. The control is
   * disabled, so this is unreachable today — and it is the difference between a
   * row contributing nothing and the whole confirm handing over a project with no
   * products, which `seededLinesForProjects` would silently drop.
   */
  const chosen = rows.filter((row) => isSeedable(row) && isTicked(row));
  const lineCount = chosen.reduce((total, row) => total + row.products.length, 0);

  const noun = KIND_NOUN[kind];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Add projects to this ${noun}`}
      size="lg"
      /*
       * The ticks are real work and the backdrop is the easiest thing on screen
       * to hit by accident at 320px. Escape still closes.
       */
      dismissOnBackdrop={false}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm(chosen)}
            disabled={loading || error !== null}
          >
            {lineCount === 0
              ? 'Continue without lines'
              : lineCount === 1
                ? 'Continue with 1 line'
                : `Continue with ${lineCount} lines`}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4">
        <p className="text-sm text-fg-secondary">
          Company: <span className="font-medium text-fg">{companyName}</span>
        </p>

        {error !== null ? (
          /*
           * ⚠️ **AN ERROR IS NOT AN EMPTY LIST.** A failed read cannot tell us
           * whether this company has projects of interest, so the dialog stays
           * open with a retry AND a way past. Falling through to the form would
           * silently drop the whole feature exactly when the network is
           * unreliable, and nobody reports a dialog they never knew was meant to
           * appear.
           */
          <Notice tone="danger">
            {/* The actions sit under the sentence rather than in `action`, which
                is `shrink-0` beside the text and would crush it at 320px. */}
            <p className="text-sm">{error}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={onRetry}>
                Try again
              </Button>
              <Button variant="ghost" size="sm" onClick={() => onConfirm([])}>
                Continue without lines
              </Button>
            </div>
          </Notice>
        ) : loading ? (
          /*
           * Skeletons in the real shape — a title line and a product line per
           * row — rather than a spinner, so the dialog does not change size when
           * the reads land.
           */
          <ul className="grid gap-3" aria-hidden>
            {[0, 1, 2].map((index) => (
              <li key={index} className="grid gap-2 rounded-md border border-border p-3">
                <Skeleton className="h-4 w-48" />
                <Skeleton className="h-3 w-64" />
              </li>
            ))}
          </ul>
        ) : (
          <ul className="grid gap-3">
            {rows.map((row) => (
              <li
                key={row.id}
                className={cx(
                  'grid gap-2 rounded-md border p-3',
                  isSeedable(row) ? 'border-border' : 'border-hairline-strong bg-surface-sunken',
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Checkbox
                    checked={isTicked(row)}
                    disabled={!isSeedable(row)}
                    onChange={(event) =>
                      setTicked((current) => ({ ...current, [row.id]: event.target.checked }))
                    }
                    label={<span className="text-sm font-medium text-fg">{row.name}</span>}
                  />

                  {/*
                   * ⚠️ NOT helper text — it names a state nothing else on this
                   * row shows. Without it a project this company has already been
                   * quoted for reads as an untouched opportunity, and the reader
                   * seeds a second document for work already on one.
                   */}
                  {row.sold !== null && row.sold !== 'none' && (
                    <Badge tone="warning">{SOLD_LABEL[row.sold]}</Badge>
                  )}
                </div>

                {isSeedable(row) ? (
                  /*
                   * What this row will actually add, in the order it will add it
                   * — the products are already ordered participation-first and
                   * travel last by the caller. A fact about the row, in the same
                   * class as the units span on the combine dialog: one line per
                   * product, so the reader can see a two-line row before ticking.
                   */
                  <p className="text-xs text-fg-muted">
                    {row.products.map((product) => product.name).join(' · ')}
                  </p>
                ) : (
                  /*
                   * ⚠️ WHY the row cannot be chosen, which a disabled tickbox
                   * cannot say. Without it an unreadable project looks identical
                   * to a project nobody has set up — and every project sells at
                   * least one product, so "sells nothing" is never the answer.
                   */
                  <p className="text-xs text-fg-muted">
                    What this project sells could not be read, so no line can be added from it.
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}

        {error === null && !loading && soldCheckSkippedFor !== null && (
          <p className="text-xs text-fg-muted">
            {soldCheckSkippedFor === 1
              ? 'This company holds 1 project, so it was not checked against existing quotes and invoices.'
              : `This company holds ${soldCheckSkippedFor} projects, so they were not checked against existing quotes and invoices.`}{' '}
            A row with no marker may still have been sold.
          </p>
        )}

        {error === null && !loading && soldCheckIncomplete && (
          <p className="text-xs text-fg-muted">
            Some projects could not be checked against existing quotes and invoices. A row with no
            marker may still have been sold.
          </p>
        )}
      </div>
    </Modal>
  );
}
